import React from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';

import COLOR from '@CONSTANTS/COLOR.constant';

import styles from './CircularProgressIndicator.module.scss';

const cx: CX = classnames.bind(styles);

function CircularProgressIndicator(props: Props) {
    const { color, size, thickness } = props;

    // Calculate derived values for the SVG
    const center = size / 2;
    const radius = (size - thickness * 2) / 2;
    const circumference = 2 * Math.PI * radius;
    
    // Create gradient ID based on color to ensure uniqueness
    const gradientId = `gradient-${color.replace('#', '')}`;
    const glowId = `glow-${color.replace('#', '')}`;

    return (
        <div 
            className={cx('circular-progress-indicator')}
            style={{ 
                width: size, 
                height: size,
                '--loader-color': color,
                '--loader-size': `${size}px`,
                '--loader-thickness': `${thickness}px`
            } as React.CSSProperties}
        >
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className={cx('svg-loader')}
            >
                <defs>
                    {/* Main gradient */}
                    <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={color} stopOpacity="0.1" />
                        <stop offset="50%" stopColor={color} stopOpacity="0.8" />
                        <stop offset="100%" stopColor={color} stopOpacity="1" />
                    </linearGradient>
                    
                    {/* Glow filter */}
                    <filter id={glowId}>
                        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                        <feMerge> 
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Background circle */}
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={thickness / 3}
                    strokeOpacity="0.1"
                    className={cx('background-circle')}
                />

                {/* Main animated circle */}
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke={`url(#${gradientId})`}
                    strokeWidth={thickness}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * 0.25}
                    className={cx('main-circle')}
                    filter={`url(#${glowId})`}
                />

                {/* Secondary rotating circle */}
                <circle
                    cx={center}
                    cy={center}
                    r={radius * 0.7}
                    fill="none"
                    stroke={color}
                    strokeWidth={thickness / 2}
                    strokeLinecap="round"
                    strokeOpacity="0.6"
                    strokeDasharray={`${circumference * 0.3} ${circumference * 0.7}`}
                    className={cx('secondary-circle')}
                />

                {/* Inner pulsing dots */}
                <g className={cx('dots-container')}>
                    <circle
                        cx={center}
                        cy={center - radius * 0.5}
                        r={thickness / 2}
                        fill={color}
                        className={cx('dot', 'dot-1')}
                    />
                    <circle
                        cx={center + radius * 0.35}
                        cy={center + radius * 0.35}
                        r={thickness / 2}
                        fill={color}
                        className={cx('dot', 'dot-2')}
                    />
                    <circle
                        cx={center - radius * 0.35}
                        cy={center + radius * 0.35}
                        r={thickness / 2}
                        fill={color}
                        className={cx('dot', 'dot-3')}
                    />
                </g>

                {/* Central glow */}
                <circle
                    cx={center}
                    cy={center}
                    r={thickness}
                    fill={color}
                    fillOpacity="0.3"
                    className={cx('center-glow')}
                />
            </svg>
        </div>
    );
}

CircularProgressIndicator.defaultProps = {
    color: COLOR['app-white'],
    size: 35,
    thickness: 3,
};

CircularProgressIndicator.propTypes = {
    color: PropTypes.string,
    size: PropTypes.number,
    thickness: PropTypes.number,
};

type Props = InferProps<typeof CircularProgressIndicator.propTypes> & typeof CircularProgressIndicator.defaultProps;

export default CircularProgressIndicator;
